import React from "react";
import { Link } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import {
  BookOpen, ArrowLeft, Upload, Mail, Sparkles, FileCode2, Send,
  Cloud, Coins, Users, Gift, ShieldCheck, Building2, Zap,
} from "lucide-react";

const SECTIONS = [
  { id: "panoramica", label: "Panoramica", icon: BookOpen },
  { id: "registrazione", label: "Registrazione e profilo", icon: ShieldCheck },
  { id: "upload", label: "Caricamento fatture", icon: Upload },
  { id: "inbound", label: "Email dedicata", icon: Mail },
  { id: "ai", label: "Estrazione AI", icon: Sparkles },
  { id: "xml", label: "Generazione XML FatturaPA", icon: FileCode2 },
  { id: "sdi", label: "Invio SDI", icon: Send },
  { id: "fic", label: "Fatture in Cloud", icon: Cloud },
  { id: "crediti", label: "Sistema crediti", icon: Coins },
  { id: "commercialista", label: "Multi-azienda commercialisti", icon: Users },
  { id: "referral", label: "Programma referral", icon: Gift },
  { id: "sicurezza", label: "Sicurezza e privacy", icon: Building2 },
];

export default function Docs() {
  useSeo({
    title: "Docs autofattura estera SDI",
    description: "Documentazione completa della piattaforma Autofattura: upload, estrazione AI, XML FatturaPA, invio SDI, integrazioni Fatture in Cloud, sistema crediti, referral",
    path: "/docs",
  });

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="docs-page">
      {/* Top nav */}
      <header className="sticky top-0 z-30 bg-[#FAF9F6]/85 backdrop-blur-md border-b border-[#E8E4D9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm font-semibold text-[#0A0A0A] hover:text-[#1D4ED8] transition-colors" data-testid="docs-back-link">
            <ArrowLeft className="w-4 h-4" /> Torna al sito
          </Link>
          <div className="flex items-center gap-6 text-sm font-medium text-[#4A4A4A]">
            <Link to="/login" className="hover:text-[#0A0A0A]">Accedi</Link>
            <Link to="/register" className="px-4 py-1.5 rounded-full bg-[#0A0A0A] text-white hover:bg-[#1D4ED8] transition-colors">Prova gratis</Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10">
        {/* TOC sidebar */}
        <aside className="lg:sticky lg:top-24 self-start" data-testid="docs-toc">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#1D4ED8] font-bold mb-3">
            <span className="w-4 h-px bg-[#1D4ED8]" /> Indice
          </div>
          <nav className="space-y-0.5">
            {SECTIONS.map(({ id, label, icon: Icon }) => (
              <a
                key={id}
                href={`#${id}`}
                data-testid={`docs-toc-${id}`}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[#4A4A4A] hover:bg-white hover:text-[#0A0A0A] transition-colors"
              >
                <Icon className="w-4 h-4 text-[#737373]" />
                {label}
              </a>
            ))}
          </nav>
        </aside>

        <main className="min-w-0">
          {/* Hero */}
          <div className="mb-12">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#1D4ED8] font-bold mb-4">
              <span className="w-6 h-px bg-[#1D4ED8]" /> Documentazione
            </div>
            <h1 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tighter leading-tight text-[#0A0A0A]" data-testid="docs-title">
              Come funziona <span className="font-serif-accent text-[#1D4ED8]">Autofattura</span>
            </h1>
            <p className="mt-4 text-lg text-[#4A4A4A] max-w-2xl">
              Guida completa a tutte le funzionalit&agrave; della piattaforma: dal caricamento del PDF all&apos;invio SDI, passando per l&apos;estrazione AI e le integrazioni contabili
            </p>
          </div>

          {/* Sections */}
          <Section id="panoramica" icon={BookOpen} title="Panoramica">
            <p>
              Autofattura &egrave; una piattaforma SaaS che automatizza la gestione delle <strong>autofatture per acquisti di beni e servizi dall&apos;estero</strong> (Google Ads, Meta, Amazon, Hetzner, ecc.), obbligatorie per legge per Partite IVA italiane.
            </p>
            <p>
              Trasforma i PDF ricevuti dai fornitori in file XML conformi allo standard <strong>FatturaPA</strong> (versione 1.2.2) e li invia automaticamente al Sistema di Interscambio (SDI) dell&apos;Agenzia delle Entrate.
            </p>
            <FeatureGrid items={[
              { icon: Zap, title: "12 secondi", text: "Dal PDF caricato all'XML pronto" },
              { icon: ShieldCheck, title: "Conforme SDI", text: "TD17 / TD18 / TD19 automatici" },
              { icon: Cloud, title: "Integrato", text: "Fatture in Cloud e OpenAPI.it" },
            ]} />
          </Section>

          <Section id="registrazione" icon={ShieldCheck} title="Registrazione e profilo aziendale">
            <p>
              Alla registrazione richiediamo <strong>8 campi obbligatori</strong> per generare autofatture conformi:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Denominazione o ragione sociale</li>
              <li>Partita IVA (11 cifre, verificata via VIES)</li>
              <li>Codice Fiscale</li>
              <li>PEC (posta elettronica certificata)</li>
              <li>Indirizzo, numero civico, CAP, comune e provincia</li>
            </ul>
            <Callout tone="info">
              Il sistema effettua un <strong>lookup automatico VIES</strong>: appena inserisci la P.IVA precompiliamo denominazione e sede legale
            </Callout>
            <p>
              Una volta creato il profilo, i dati fiscali sono <strong>bloccati per motivi di conformit&agrave;</strong>. Puoi modificare autonomamente solo: PEC, codice destinatario SDI, sezionale e numerazione autofatture. Per gli altri dati contatta il supporto
            </p>
          </Section>

          <Section id="upload" icon={Upload} title="Caricamento fatture">
            <p>
              Puoi caricare fatture in <strong>due modi</strong>:
            </p>
            <ol className="list-decimal pl-5 space-y-2">
              <li><strong>Upload manuale</strong>: dalla pagina &quot;Carica PDF&quot; puoi trascinare fino a <strong>50 file PDF alla volta</strong>, ognuno di massimo 10 MB</li>
              <li><strong>Inoltro automatico via email</strong>: ogni azienda ha un indirizzo email dedicato (vedi sezione successiva)</li>
            </ol>
            <p>
              Ogni fattura caricata consuma <strong>1 credito</strong> per l&apos;estrazione AI. Se il saldo &egrave; a 0 il caricamento viene bloccato con un messaggio di ricarica
            </p>
          </Section>

          <Section id="inbound" icon={Mail} title="Email dedicata per ricezione fatture">
            <p>
              Ogni azienda ha un indirizzo email univoco del tipo <code className="font-mono bg-[#F5F2EA] px-1.5 py-0.5 rounded text-sm">nome-azienda@autofattura.dasabo.com</code>
            </p>
            <p>
              Puoi configurare Google, Meta, Amazon per ricevere le fatture direttamente a questo indirizzo, oppure inoltrare manualmente. Il sistema processa gli allegati PDF, li scarica e li invia all&apos;estrazione AI in modo trasparente
            </p>
            <Callout tone="info">
              Se hai il profilo commercialista, ogni azienda cliente avr&agrave; il suo indirizzo personalizzato per mantenere le fatture perfettamente isolate
            </Callout>
          </Section>

          <Section id="ai" icon={Sparkles} title="Estrazione AI e revisione">
            <p>
              L&apos;estrazione automatica utilizza modelli AI di ultima generazione (Claude Sonnet, Gemini, GPT) per riconoscere:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Dati del fornitore estero (nome, VAT ID, indirizzo, paese ISO)</li>
              <li>Numero fattura, data, valuta originale</li>
              <li>Importi, aliquote, natura fiscale</li>
              <li>Descrizione del servizio o del bene</li>
              <li>Conversione automatica in EUR via tassi Banca d&apos;Italia / ECB</li>
            </ul>
            <p>
              Dopo l&apos;estrazione ricevi una email di notifica e puoi revisionare i dati in un editor completo. Se l&apos;estrazione fallisce (PDF di scarsa qualit&agrave;) ti avvisiamo e puoi compilare manualmente in un minuto
            </p>
          </Section>

          <Section id="xml" icon={FileCode2} title="Generazione XML FatturaPA">
            <p>
              Cliccando &quot;Genera XML&quot; il sistema produce un file conforme alla specifica <strong>FatturaPA 1.2.2</strong> pronto per lo SDI. Tipi documento supportati:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 my-4">
              <TdCard code="TD17" title="Servizi esteri" text="Integrazione per acquisto servizi extra-UE ed intra-UE" />
              <TdCard code="TD18" title="Beni intra-UE" text="Integrazione per acquisto di beni intracomunitari" />
              <TdCard code="TD19" title="Beni ex art. 17" text="Integrazione per acquisto beni ex art. 17 c. 2 DPR 633/72" />
            </div>
            <p>
              Il reverse-charge al 22% viene applicato automaticamente sulle fatture estere a 0% IVA (natura N6.9 / N2.1). La numerazione autofattura segue il formato <code className="font-mono bg-[#F5F2EA] px-1.5 py-0.5 rounded text-sm">SEZIONALE N/AA</code> (es. AF01 1/26) con reset automatico ad ogni anno solare
            </p>
          </Section>

          <Section id="sdi" icon={Send} title="Invio SDI">
            <p>
              L&apos;invio al Sistema di Interscambio avviene tramite l&apos;integrazione diretta con <strong>OpenAPI.it</strong> (intermediario accreditato):
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Firma digitale automatica (endpoint <code className="font-mono text-xs bg-[#F5F2EA] px-1 py-0.5 rounded">/invoices_signature</code>)</li>
              <li>Conservazione a norma opzionale</li>
              <li>Webhook realtime per gli stati SDI (RC / NS / MC / DT)</li>
              <li>Email automatica in caso di scarto (NS) con motivo tradotto in italiano semplice</li>
            </ul>
            <p>
              In alternativa puoi marcare una fattura come <strong>trasmessa manualmente</strong> (se hai gi&agrave; il tuo canale SDI). Le fatture trasmesse diventano immutabili per legge, ma se lo SDI le scarta le riapriamo automaticamente per la correzione
            </p>
          </Section>

          <Section id="fic" icon={Cloud} title="Integrazione Fatture in Cloud">
            <p>
              Se usi <strong>Fatture in Cloud</strong> puoi collegare l&apos;account in un click dalla sezione Integrazioni. Il flusso OAuth 2.0 &egrave; sicuro (token cifrati at-rest con Fernet) e con auto-refresh dei token di accesso
            </p>
            <p>
              Una volta collegato, sulla pagina di revisione di ogni fattura appare il bottone <strong>&quot;Invia a Fatture in Cloud&quot;</strong>: la fattura viene creata come autofattura in FIC con XML pre-generato, e FIC la trasmette allo SDI per te. Modalit&agrave; dry-run disponibile per test senza impatti
            </p>
          </Section>

          <Section id="crediti" icon={Coins} title="Sistema crediti">
            <p>
              Il servizio funziona a <strong>crediti prepagati</strong>: ogni estrazione AI consuma 1 credito. Al signup ricevi <strong>5 crediti omaggio</strong>
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-4">
              <PriceCard name="Starter" credits="25" price="€ 14,90" />
              <PriceCard name="Business" credits="60" price="€ 24,90" popular />
              <PriceCard name="Pro" credits="150" price="€ 39,90" />
              <PriceCard name="Enterprise" credits="500" price="€ 89,90" />
            </div>
            <p className="text-xs text-[#737373]">
              Tutti i prezzi sono IVA inclusa. Pagamenti sicuri via Stripe. Ricevi automaticamente un documento proforma PDF per ogni ordine; la fattura fiscale sar&agrave; emessa da DASABO SRL e trasmessa via SDI al Codice Destinatario / PEC configurato
            </p>
          </Section>

          <Section id="commercialista" icon={Users} title="Multi-azienda per commercialisti">
            <p>
              Se in fase di registrazione hai attivato il toggle <strong>&quot;Sono un commercialista&quot;</strong> puoi gestire <strong>pi&ugrave; aziende clienti</strong> sotto un unico account:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Ogni azienda ha il suo profilo fiscale, numerazione autofatture e indirizzo email dedicato</li>
              <li>Le fatture sono isolate per azienda (filtrate dall&apos;azienda attiva)</li>
              <li>I <strong>crediti sono globali</strong> sull&apos;account: acquisti una volta, li usi su tutte le aziende</li>
              <li>Passi da un&apos;azienda all&apos;altra dalla sezione &quot;Il tuo Account &rsaquo; Aziende&quot; con un click</li>
            </ul>
            <Callout tone="warning">
              Gli utenti normali (non commercialisti) possono avere <strong>una sola azienda</strong>. Per sbloccare il profilo commercialista contatta il supporto
            </Callout>
          </Section>

          <Section id="referral" icon={Gift} title="Programma referral">
            <p>
              Ogni account ha un <strong>codice referral univoco</strong> (formato <code className="font-mono text-xs bg-[#F5F2EA] px-1 py-0.5 rounded">AFXXXXXX</code>) che puoi condividere
            </p>
            <ol className="list-decimal pl-5 space-y-2">
              <li>Un nuovo utente si registra usando il tuo codice</li>
              <li>Riceve i suoi 5 crediti di benvenuto standard</li>
              <li>Quando fa il <strong>primo acquisto di un pacchetto</strong> scattano i bonus:
                <ul className="list-disc pl-5 mt-1 space-y-0.5">
                  <li>+5 crediti bonus per lui/lei</li>
                  <li>+5 crediti bonus per te (che l&apos;hai invitato)</li>
                </ul>
              </li>
            </ol>
            <p>
              Il bonus viene erogato una sola volta al primo acquisto (idempotente). Trovi il tuo codice e il link da condividere sotto <strong>Il tuo Account &rsaquo; Referral</strong>
            </p>
          </Section>

          <Section id="sicurezza" icon={Building2} title="Sicurezza e privacy">
            <p>
              Tutti i dati sono trattati secondo GDPR ed archiviati in Europa (MongoDB Atlas EU-West-1). La password &egrave; hashata con bcrypt, l&apos;autenticazione JWT scade e si rinnova periodicamente
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Retention</strong>: le fatture sono conservate 10 anni (obbligo fiscale), i log tecnici 12 mesi</li>
              <li><strong>Cifratura at rest</strong>: token FIC criptati con Fernet, database criptato lato hosting</li>
              <li><strong>Nessuna profilazione</strong>: solo cookie tecnici, nessun tracker di terze parti</li>
              <li><strong>Diritto all&apos;oblio</strong>: puoi richiedere la cancellazione del tuo account in ogni momento scrivendo a <a href="mailto:privacy@dasabo.com" className="text-[#1D4ED8] hover:underline">privacy@dasabo.com</a></li>
            </ul>
            <p>
              Per il dettaglio completo consulta <Link to="/privacy" className="text-[#1D4ED8] hover:underline">Privacy Policy</Link>, <Link to="/gdpr" className="text-[#1D4ED8] hover:underline">GDPR</Link>, <Link to="/cookies" className="text-[#1D4ED8] hover:underline">Cookie Policy</Link>
            </p>
          </Section>

          <div className="mt-16 pt-8 border-t border-[#E8E4D9] text-center">
            <p className="text-[#4A4A4A]">Non hai trovato quello che cercavi?</p>
            <a
              href="mailto:supporto@dasabo.com"
              data-testid="docs-support-link"
              className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-[#1D4ED8] transition-colors"
            >
              Scrivi al supporto
            </a>
          </div>
        </main>
      </div>
    </div>
  );
}

function Section({ id, icon: Icon, title, children }) {
  return (
    <section id={id} className="mb-14 scroll-mt-24" data-testid={`docs-section-${id}`}>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-[#1D4ED8]/10 text-[#1D4ED8] grid place-items-center">
          <Icon className="w-5 h-5" />
        </div>
        <h2 className="font-display font-extrabold text-2xl lg:text-3xl tracking-tight text-[#0A0A0A]">
          {title}
        </h2>
      </div>
      <div className="space-y-4 text-[#4A4A4A] leading-relaxed max-w-3xl">
        {children}
      </div>
    </section>
  );
}

function Callout({ tone = "info", children }) {
  const cls = tone === "warning"
    ? "border-amber-200 bg-amber-50 text-amber-900"
    : "border-blue-200 bg-blue-50 text-blue-900";
  return (
    <div className={`rounded-xl border ${cls} p-4 text-sm`}>{children}</div>
  );
}

function FeatureGrid({ items }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 my-4">
      {items.map((it, i) => (
        <div key={it.title || i} className="rounded-xl border border-[#E8E4D9] bg-white p-4">
          <div className="w-9 h-9 rounded-lg bg-[#1D4ED8]/10 text-[#1D4ED8] grid place-items-center mb-3">
            <it.icon className="w-4 h-4" />
          </div>
          <div className="font-display font-bold text-[#0A0A0A]">{it.title}</div>
          <div className="text-xs text-[#737373] mt-1">{it.text}</div>
        </div>
      ))}
    </div>
  );
}

function TdCard({ code, title, text }) {
  return (
    <div className="rounded-xl border border-[#E8E4D9] bg-white p-4">
      <div className="font-mono text-xs font-bold text-[#1D4ED8]">{code}</div>
      <div className="font-display font-bold text-[#0A0A0A] mt-1">{title}</div>
      <div className="text-xs text-[#737373] mt-1">{text}</div>
    </div>
  );
}

function PriceCard({ name, credits, price, popular }) {
  return (
    <div className={`rounded-xl border p-4 relative ${popular ? "border-[#1D4ED8] bg-blue-50/40" : "border-[#E8E4D9] bg-white"}`}>
      {popular && (
        <div className="absolute -top-2 left-3 px-2 py-0.5 rounded-full bg-[#1D4ED8] text-white text-[9px] font-bold uppercase tracking-wider">
          Popolare
        </div>
      )}
      <div className="font-display font-bold text-[#0A0A0A]">{name}</div>
      <div className="mt-1 text-xs text-[#737373]">{credits} crediti</div>
      <div className="mt-2 font-mono font-extrabold text-lg text-[#0A0A0A]">{price}</div>
    </div>
  );
}
